import { FileTypeEnum } from '../../enums/FileTypeEnum';
import type FileWithToken from '../../interfaces/FileWithToken';
import { isNoteExtension } from '../../utils/utils';

/** Normalize extension to leading-dot lower-case (e.g. "MSG" → ".msg"). */
function normalizeExtension(fileExtension: string): string {
  return fileExtension.startsWith('.')
    ? fileExtension.toLowerCase()
    : `.${fileExtension.toLowerCase()}`;
}

/**
 * Dedicated preview viewers keyed by extension. Backend often returns Text/Code for these;
 * force the dedicated FileType regardless (same idea as isNoteExtension).
 */
function dedicatedViewerFileType(fileExtension: string | undefined): FileTypeEnum | null {
  if (fileExtension == null || fileExtension === '') return null;
  switch (normalizeExtension(fileExtension)) {
    case '.eml':
      return FileTypeEnum.Eml;
    case '.ics':
      return FileTypeEnum.Ics;
    case '.vcf':
    case '.vcard':
      return FileTypeEnum.Vcf;
    case '.msg':
      return FileTypeEnum.Msg;
    default:
      return null;
  }
}

/**
 * Canonicalize viewer fileType from API metadata + extension overrides.
 * - Unknown-only: Secret, Wopitest
 * - Extension always wins: Eml / Ics / Vcf / Msg (and notes via isNoteExtension)
 */
export function normalizeViewerFile(response: FileWithToken): FileWithToken {
  let normalizedFile: FileWithToken = response;

  if (response.fileType === FileTypeEnum.Unknown) {
    const ext = normalizeExtension(response.fileExtension ?? '');
    if (ext === '.fgsecret') {
      normalizedFile = { ...response, fileType: FileTypeEnum.Secret };
    } else if (ext === '.wopitest') {
      normalizedFile = { ...response, fileType: FileTypeEnum.Wopitest };
    }
  }

  const dedicatedType = dedicatedViewerFileType(response.fileExtension);
  if (dedicatedType != null) {
    normalizedFile = { ...normalizedFile, fileType: dedicatedType };
  }

  // Note files open in the WYSIWYG note editor regardless of backend type.
  if (isNoteExtension(response.fileExtension)) {
    normalizedFile = { ...normalizedFile, fileType: FileTypeEnum.Note };
  }

  return normalizedFile;
}
